import { useEffect, useState } from "react";
import type { SiteSettings } from "@tih/shared";
import {
  ApiError,
  adminFetchSettingsGeneral,
  adminPutSettingsGeneral,
} from "../../../api/client";
import { useSiteSettings } from "../../../context/SiteSettingsContext";

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function fromLocalInput(local: string): string {
  return new Date(local).toISOString();
}

export function AdminSettingsGeneralPage() {
  const { setSettings: cacheSettings } = useSiteSettings();
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [launchLocal, setLaunchLocal] = useState("");
  const [announcementEnabled, setAnnouncementEnabled] = useState(false);
  const [announcementTitle, setAnnouncementTitle] = useState("IMPORTANT ANNOUNCEMENT");
  const [announcementBody, setAnnouncementBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    adminFetchSettingsGeneral()
      .then((s) => {
        setSettings(s);
        setLaunchLocal(toLocalInput(s.launchAt));
        setAnnouncementEnabled(s.announcementEnabled);
        setAnnouncementTitle(s.announcementTitle);
        setAnnouncementBody(s.announcementBody);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      );
  }, []);

  async function update(patch: Partial<SiteSettings>) {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const next = await adminPutSettingsGeneral(patch);
      setSettings(next);
      setLaunchLocal(toLocalInput(next.launchAt));
      setAnnouncementEnabled(next.announcementEnabled);
      setAnnouncementTitle(next.announcementTitle);
      setAnnouncementBody(next.announcementBody);
      cacheSettings(next);
      setSuccess(
        patch.mode === "live"
          ? "Full public site is open."
          : patch.mode === "coming-soon"
            ? "Coming-soon page is on for anonymous visitors."
            : "Settings saved.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save settings");
    } finally {
      setBusy(false);
    }
  }

  if (!settings) {
    return <div style={{ opacity: 0.5 }}>Loading…</div>;
  }

  const isLive = settings.mode === "live";
  const launchDate = new Date(settings.launchAt);

  return (
    <div>
      <div className={`site-status site-status-${settings.mode}`}>
        <div className="site-status-dot" aria-hidden="true" />
        <div className="site-status-text">
          <strong>
            {isLive
              ? "Full public site is open"
              : "Visitors see the coming-soon page"}
          </strong>
          <span>
            {isLive
              ? "Anyone can browse the full site (story, gallery, blog, and more)."
              : "Anonymous visitors get directions, the upcoming event, and live; signed-in members still see the full site."}
          </span>
        </div>
        <button
          type="button"
          className={`btn ${isLive ? "btn-ghost" : "btn-primary"}`}
          disabled={busy}
          onClick={() => void update({ mode: isLive ? "coming-soon" : "live" })}
        >
          {busy
            ? "Saving…"
            : isLive
              ? "Back to coming-soon"
              : "Open full site"}
        </button>
      </div>

      <section className="admin-card">
        <h2 className="admin-card-title">Launch countdown</h2>
        {settings.countdownSource === "event" && (
          <p className="admin-card-sub" style={{ marginBottom: 12, color: "var(--gold, #c9a227)" }}>
            The public countdown currently follows the <strong>next scheduled event</strong>{" "}
            in <a href="/admin/events">Admin → Events</a>. The datetime below is still saved as
            a fallback when no future event exists.
          </p>
        )}
        <p className="admin-card-sub">
          Shown on the coming-soon page. Stored as UTC; entered in your local
          timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone}).
        </p>
        <div className="admin-card-row">
          <label className="admin-field">
            <span>Launch date &amp; time</span>
            <input
              type="datetime-local"
              value={launchLocal}
              onChange={(e) => setLaunchLocal(e.target.value)}
              disabled={busy}
            />
          </label>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !launchLocal}
            onClick={() => void update({ launchAt: fromLocalInput(launchLocal) })}
          >
            {busy ? "Saving…" : "Save countdown"}
          </button>
        </div>
        <p className="admin-card-foot">
          Currently counting down to{" "}
          <strong>{launchDate.toLocaleString()}</strong>{" "}
          (
          {launchDate.toLocaleString("en-GB", {
            timeZone: "Africa/Kampala",
            dateStyle: "medium",
            timeStyle: "short",
          })}{" "}
          Kampala).
        </p>
      </section>


      <section className="admin-card">
        <h2 className="admin-card-title">Important Updates banner</h2>
        <p className="admin-card-sub">
          Important Updates for the whole site (e.g. this week cancelled, next month new venue).
          Separate from Events — cancel an overnight on the event itself; create the next night as a new event.
          Do not edit a cancelled night&apos;s date into the next gathering.
        </p>

        <label className="admin-accent-live-toggle" style={{ marginBottom: 12, display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="checkbox"
            checked={announcementEnabled}
            disabled={busy}
            onChange={(e) => setAnnouncementEnabled(e.target.checked)}
          />
          <span>Show Important Updates banner</span>
        </label>
        <div className="admin-blog-form-fields" style={{ display: "grid", gap: 12 }}>
          <label className="admin-field">
            <span>Title</span>
            <input
              type="text"
              value={announcementTitle}
              disabled={busy}
              maxLength={120}
              onChange={(e) => setAnnouncementTitle(e.target.value)}
              placeholder="IMPORTANT ANNOUNCEMENT"
            />
          </label>
          <label className="admin-field">
            <span>Body</span>
            <textarea
              rows={4}
              maxLength={2000}
              value={announcementBody}
              disabled={busy}
              onChange={(e) => setAnnouncementBody(e.target.value)}
              placeholder="This Friday's overnight is cancelled. Next gathering in October…"
            />
          </label>
        </div>
        <div className="admin-card-row" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !announcementTitle.trim() || !announcementBody.trim()}
            onClick={() =>
              void update({
                announcementEnabled,
                announcementTitle: announcementTitle.trim(),
                announcementBody: announcementBody.trim(),
              })
            }
          >
            {busy ? "Saving…" : "Save announcement"}
          </button>
        </div>
      </section>

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success" style={{ padding: 12 }}>
          {success}
        </div>
      )}
    </div>
  );
}
